import { Button } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Modal } from 'antd'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import {
  cloneElement,
  type ReactElement,
  type ReactNode,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type ConfirmationDialogProps = {
  /**
   * Trigger element (usually a Button). Its `onClick` is treated as the confirm
   * action — clicking it opens the dialog, and the action only runs once the
   * user confirms.
   */
  children?: ReactElement
  /** Controlled mode, for a dialog opened from code rather than by a trigger. */
  open?: boolean
  onConfirm?: () => void
  onCancel?: () => void
  title: string
  description: ReactNode
  confirmText: string
  cancelText?: string
  confirmVariant?: 'default' | 'primary' | 'danger'
  width?: number
  testId?: string
}

/**
 * Confirmation dialog matching the current design language (sentence-case title,
 * header + footer dividers, right-aligned actions). Built on the plain antd
 * `Modal` rather than the ui-kit `Modal`, whose global styles force an uppercase
 * pink title and strip the dividers. Not backdrop-dismissible — the user must
 * pick an action.
 */
const ConfirmationDialog = ({
  children,
  open: controlledOpen,
  onConfirm,
  onCancel,
  title,
  description,
  confirmText,
  cancelText,
  confirmVariant = 'danger',
  width = 580,
  testId
}: ConfirmationDialogProps) => {
  const { t } = useTranslation(NAMESPACE.GENERAL)
  const [open, setOpen] = useState(false)

  const isControlled = controlledOpen !== undefined

  const close = () => {
    if (isControlled) onCancel?.()
    else setOpen(false)
  }

  const handleConfirm = () => {
    if (isControlled) {
      onConfirm?.()
      return
    }
    children?.props.onClick?.()
    setOpen(false)
  }

  return (
    <>
      {children && cloneElement(children, { onClick: () => setOpen(true) })}
      <Modal
        open={isControlled ? controlledOpen : open}
        onCancel={close}
        footer={null}
        width={width}
        destroyOnClose
        maskClosable={false}
        data-testid={testId ? `${testId}-dialog` : undefined}
      >
        <Title>{title}</Title>
        <Divider />
        <Body>{description}</Body>
        <Divider />
        <FooterRow>
          <Button
            onClick={close}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            {cancelText ?? t(generalConstants.CANCEL)}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {confirmText}
          </Button>
        </FooterRow>
      </Modal>
    </>
  )
}

const Title = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
  color: ${({ theme }) => theme.color.neutral.color2};
  margin-bottom: 16px;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 24px 0;
`

const FooterRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 16px;
`

export default ConfirmationDialog
